import { ApiError } from "@/lib/api";

/**
 * Framework-agnostic mapping from errors to plain-language, user-facing text.
 * Never surfaces raw HTTP status codes, backend error codes, or arbitrary
 * `Error.message` prose — those may leak internals or read as jargon.
 */

const DEFAULT_FALLBACK = "Something went wrong. Please try again.";

const STATUS_MESSAGES: Record<number, string> = {
  400: "That request didn't look right. Please check your input and try again.",
  401: "You need to sign in to continue.",
  403: "You don't have permission to do that.",
  404: "We couldn't find what you're looking for.",
  409: "That doesn't match the latest information. Please refresh and try again.",
  422: "Some of the information provided isn't valid. Please check and try again.",
  429: "You're doing that too much — please wait a moment and try again.",
  500: "Something went wrong on our end. Please try again in a moment.",
  502: "Something went wrong on our end. Please try again in a moment.",
  503: "Something went wrong on our end. Please try again in a moment.",
  504: "Something went wrong on our end. Please try again in a moment.",
};

// Recognized backend error codes get their own, more specific message.
const CODE_MESSAGES: Record<string, string> = {
  PRE_ASSESSMENT_REQUIRED: "You'll need to complete the pre-assessment before you can access this session.",
  EMAIL_TAKEN: "An account with this email already exists. Try signing in instead.",
};

/**
 * Resolve any thrown value into a short, plain-language sentence safe to show
 * a non-technical user. Never returns a raw HTTP status, error code, or the
 * underlying `Error.message` — only known, curated copy or `fallback`.
 */
export function getFriendlyErrorMessage(err: unknown, fallback: string = DEFAULT_FALLBACK): string {
  if (err instanceof ApiError) {
    if (err.code && CODE_MESSAGES[err.code]) {
      return CODE_MESSAGES[err.code];
    }
    return STATUS_MESSAGES[err.status] ?? fallback;
  }

  // Plain Error / unknown thrown value: never echo `.message` — it may be raw
  // backend prose or an ALL_CAPS_ERROR_CODE that leaked into the message field.
  return fallback;
}
